import { type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from './use-auth';

interface ProtectedRouteProps {
    children: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
    const { user, loading, logout } = useAuth();

    if (loading) {
        return (
            <div className="flex h-screen items-center justify-center">
                <div className="text-center">
                    <div className="mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-primary"></div>
                    <p className="text-muted-foreground">Loading...</p>
                </div>
            </div>
        );
    }

    if (!user) {
        const redirectTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        return <Navigate to={`/login?redirect=${encodeURIComponent(redirectTo)}`} replace />;
    }

    if (!user.groups.includes('superuser')) {
        return (
            <div className="flex h-screen items-center justify-center">
                <div className="max-w-md rounded-lg border border-destructive bg-destructive/10 p-6 text-center">
                    <h2 className="mb-2 text-xl font-bold text-destructive">Access Denied</h2>
                    <p className="mb-4 text-sm text-muted-foreground">
                        You do not have permission to access this portal. Only users in the superuser group can access
                        this application.
                    </p>
                    <button
                        onClick={() => {
                            void logout();
                        }}
                        className="rounded-md bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90"
                    >
                        Logout
                    </button>
                </div>
            </div>
        );
    }

    return <>{children}</>;
}
